/**
 * Save Error Action
 *
 * Synchronous action that saves actor error to context.
 * Used in onError transitions to persist error details.
 */

import { assign } from "xstate";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

/**
 * Type guard for neverthrow Result.Err
 */
interface ResultErr {
  isErr: true;
  error: unknown;
}

function isResultErr(error: unknown): error is ResultErr {
  return (
    typeof error === "object" &&
    error !== null &&
    "isErr" in error &&
    error.isErr === true &&
    "error" in error
  );
}

/**
 * Type for error-like objects
 */
interface ErrorLike {
  message?: string;
  code?: string | number;
  retryable?: boolean;
  [key: string]: unknown;
}

function isErrorLike(value: unknown): value is ErrorLike {
  return (
    typeof value === "object" &&
    value !== null &&
    ("message" in value || "code" in value)
  );
}

/**
 * Format error to extract all useful debugging information
 */
function formatErrorDetails(error: unknown): Record<string, unknown> {
  if (!error) return { raw: null };

  // Handle Error objects
  if (error instanceof Error) {
    return {
      type: error.name || "Error",
      message: error.message,
      stack: error.stack,
      cause: error.cause,
      raw: error,
    };
  }

  // Handle neverthrow Result.Err
  if (isResultErr(error)) {
    const errValue = error.error;
    return {
      type: "ResultError",
      message: isErrorLike(errValue) ? errValue.message : String(errValue),
      code: isErrorLike(errValue) ? errValue.code : undefined,
      retryable: isErrorLike(errValue) ? errValue.retryable : undefined,
      details: errValue,
      raw: error,
    };
  }

  // Handle plain objects
  if (typeof error === "object" && error !== null) {
    const errorObj = error as Record<string, unknown>;
    return {
      type: "ObjectError",
      message: isErrorLike(error) ? error.message : JSON.stringify(error),
      ...errorObj,
      raw: error,
    };
  }

  // Handle primitives
  return {
    type: typeof error,
    message: String(error),
    raw: error,
  };
}

/**
 * Save Error Action
 *
 * Saves error from event.error to context with full formatting.
 * Extracts message, stack, code, retryable, and raw error.
 *
 * Example in flow JSON:
 * ```json
 * {
 *   "invoke": {
 *     "actor": "sendSMS",
 *     "onError": {
 *       "target": "smsError",
 *       "actions": [{ "type": "saveError", "params": { "field": "smsError" } }]
 *     }
 *   }
 * }
 * ```
 */
export const saveError: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ event }: any) => {
    // XState passes error in event.error for invoke onError
    const rawError = event.error;

    // Format error with full details
    const formattedError: Record<string, unknown> = {
      timestamp: new Date().toISOString(),
      ...formatErrorDetails(rawError),
    };

    // Explicitly preserve insurance-specific fields if they exist
    if (rawError?.insuranceStates) {
      formattedError.insuranceStates = rawError.insuranceStates;
    }
    if (rawError?.insuranceStatuses) {
      formattedError.insuranceStatuses = rawError.insuranceStatuses;
    }

    // Save both formatted and raw
    return {
      error: formattedError,
      lastError: formattedError, // Also save as lastError for history
    };
  }),
  metadata: {
    displayName: "saveError",
    description: "Save formatted error to context",
    category: "context",
  },
};
